import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listAvailabilities, subscribeToAvailabilities } from '../services/availabilityService';
import type { AvailabilityDocument } from '../models';
import { availabilityQueryKeys } from './availabilityQueryKeys';

interface SnapshotManager {
  unsub: (() => void) | null;
  listeners: number;
}

const snapshotManager: SnapshotManager = {
  unsub: null,
  listeners: 0,
};

export interface UseAvailabilityListQueryResult {
  data: AvailabilityDocument[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFetching: boolean;
}

export function useAvailabilityListQuery(): UseAvailabilityListQueryResult {
  const queryClient = useQueryClient();
  const queryKey = availabilityQueryKeys.list();

  const queryResult = useQuery<AvailabilityDocument[]>({
    queryKey,
    queryFn: listAvailabilities,
    initialData: () =>
      queryClient.getQueryData<AvailabilityDocument[]>(queryKey),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    snapshotManager.listeners += 1;

    if (!snapshotManager.unsub) {
      snapshotManager.unsub = subscribeToAvailabilities((availabilities) => {
        queryClient.setQueryData(queryKey, availabilities);
      });
    }

    return () => {
      snapshotManager.listeners -= 1;
      if (snapshotManager.listeners === 0 && snapshotManager.unsub) {
        snapshotManager.unsub();
        snapshotManager.unsub = null;
      }
    };
  }, [queryClient, queryKey]);

  return {
    data: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: (queryResult.error as Error) ?? null,
    isFetching: queryResult.isFetching,
  };
}

